import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cnmfnm.cn' },
              { '@type': 'ListItem', position: 2, name: '文章分类', item: 'https://cnmfnm.cn/category' },
              { '@type': 'ListItem', position: 3, name: category?.name || article.category, item: `https://cnmfnm.cn/category/${article.category}` },
              { '@type': 'ListItem', position: 4, name: article.title },
            ],
          },
          {
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Organization', name: article.author },
            datePublished: article.publishDate,
            image: article.coverImage,
            publisher: { '@type': 'Organization', name: '皇冠App', url: 'https://cnmfnm.cn' },
            inLanguage: 'zh-CN',
          },
        ],
      }
    : null;

  useEffect(() => {
    if (!articleJsonLd) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(articleJsonLd);
    script.id = 'schema-article';
    const existing = document.getElementById('schema-article');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-article');
      if (el) el.remove();
    };
  }, [id]);

  if (!article) {
    return (
      <PageLayout>
        <NotFound />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO
        title={`${article.title} - 皇冠App`}
        description={article.excerpt}
        keywords={`皇冠App,${article.title},皇冠App教程,皇冠App文章,${category?.name || ''}`}
        canonicalUrl={`https://cnmfnm.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
      />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章分类', path: '/category' },
                { label: category?.name || article.category, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto py-10 md:py-14">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                {category?.name || article.category}
              </span>
              <span className="text-sm text-foreground-400">{article.publishDate}</span>
              <span className="text-sm text-foreground-400">{article.readTime}阅读</span>
              <span className="text-sm text-foreground-400">作者：{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>

      <style>{`
        .prose-custom h3 { font-size: 1.35rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.75rem; color: oklch(var(--foreground-900)); font-family: var(--font-heading); }
        .prose-custom p { font-size: 1rem; line-height: 1.85; margin-bottom: 1rem; color: oklch(var(--foreground-700)); }
        .prose-custom ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
        .prose-custom li { font-size: 1rem; line-height: 1.8; color: oklch(var(--foreground-700)); margin-bottom: 0.25rem; }
        .prose-custom strong { color: oklch(var(--foreground-900)); font-weight: 600; }
        .prose-custom a { color: oklch(var(--primary-600)); text-decoration: underline; }
      `}</style>
    </PageLayout>
  );
}